import type { StepKey } from "@/types/wizard";
import { strings } from "@/lib/strings";

const WIZARD_ROOT = "/wizard";

/**
 * Absolute browser path for a wizard step. The SPA itself is hosted at `/`, while setup lives under `/wizard/*`.
 */
export function wizardRoutePath(segment: string): string {
  const seg = segment.replace(/^\/+/, "");
  return `${WIZARD_ROOT}/${seg}`;
}

/**
 * Absolute browser path for a step (e.g. `/wizard/apply`). Use this when comparing against
 * `window.location.pathname` or building a URL the bridge backend will receive verbatim.
 */
export function wizardLocationPath(segment: string): string {
  return wizardRoutePath(segment);
}

export interface StepDefinition {
  key: StepKey;
  title: string;
  description: string;
  /** Path segment under `/wizard`. */
  segment: string;
}

export const WIZARD_STEPS: readonly StepDefinition[] = [
  {
    key: "review",
    title: strings.steps.review.title,
    description: strings.steps.review.description,
    segment: "review",
  },
  {
    key: "secrets",
    title: strings.steps.secrets.title,
    description: strings.steps.secrets.description,
    segment: "secrets",
  },
  {
    key: "done",
    title: strings.steps.done.title,
    description: strings.steps.done.description,
    segment: "done",
  },
] as const;

/** Routes that use a wider main column for tables. */
export const WIDE_LAYOUT_SEGMENTS = new Set(["", "review", "preview", "apply", "secrets", "live"]);

export function stepIndex(key: StepKey): number {
  return WIZARD_STEPS.findIndex((step) => step.key === key);
}

export function nextStepPath(key: StepKey): string | null {
  const i = stepIndex(key);
  if (i < 0 || i >= WIZARD_STEPS.length - 1) return null;
  return wizardRoutePath(WIZARD_STEPS[i + 1].segment);
}

export function previousStepPath(key: StepKey): string | null {
  const i = stepIndex(key);
  if (i <= 0) return null;
  return wizardRoutePath(WIZARD_STEPS[i - 1].segment);
}
